import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Trash04Icon } from '@langchain/untitled-ui-icons';
import { Button, IconButton } from '@mui/joy';

import { isEqual } from 'lodash-es';
import { FormEvent, ReactNode, useCallback, useMemo, useState } from 'react';

import { DeleteConfirmationButton } from '@/components/Delete';
import useToast from '@/components/Toast/useToast';
import { Tooltip } from '@/design-system';
import {
  useCreatePlaygroundSettingsMutation,
  useDeletePlaygroundSettingsMutation,
  usePlaygroundSettings,
  useUpdatePlaygroundSettingsMutation,
} from '@/hooks/useSwr';
import {
  PlaygroundSavedOptionsSchema,
  PlaygroundSettingsSchema,
} from '@/types/schema';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { DEFAULT_CONFIG_ID_LOCAL_STORAGE_KEY } from '../../../constants';
import { useUpdateModelManifest } from '../hooks/useUpdateModelManifest';
import { ConfigurationCrudModal } from './ModelConfigurationCrudModal';
import ModelConfigurationDefaultButton from './ModelConfigurationDefaultButton';
import { ModelConfigurationDropdown } from './ModelConfigurationDropdown';
import { getSettingsFromManifest, getSettingsFromModelManifest } from './utils';

interface ModelSettingsPopoverLayoutProps {
  manifest?: SerializedConstructor;
  setManifest?: (manifest: SerializedConstructor) => void;
  currentConfigId: string | undefined;
  setCurrentConfigId: (id: string | undefined) => void;
  children: ReactNode;
  savedOptions: PlaygroundSavedOptionsSchema;
  setSavedOptions: (value?: PlaygroundSavedOptionsSchema) => void;
}

export function ModelSettingsPopoverLayout({
  manifest,
  setManifest,
  savedOptions,
  setSavedOptions,
  currentConfigId,
  setCurrentConfigId,
  children,
}: ModelSettingsPopoverLayoutProps) {
  const [isNewConfigModalOpen, setIsNewConfigModalOpen] = useState(false);

  const { createToast } = useToast();

  const { data: storedPlaygroundSettings } = usePlaygroundSettings();
  const hasStoredSettings = !!storedPlaygroundSettings?.length;
  const showDropdown = !!(hasStoredSettings && manifest && setManifest);

  const { updateModelManifest, resetToDefault } = useUpdateModelManifest(
    manifest,
    setManifest
  );

  const createStoredPlaygroundSettings = useCreatePlaygroundSettingsMutation({
    onSuccess: (data) => {
      setIsNewConfigModalOpen(false);
      setCurrentConfigId(data.id);
      createToast({
        title: 'Success',
        description: 'Saved current configuration',
        error: false,
      });
    },
    onError: (e) => {
      createToast({
        title: 'Error',
        description: `Unable to save current configuration: ${e.message}`,
        error: true,
      });
    },
  });

  const handleNewConfigSubmit = useCallback(
    async (e: FormEvent, name: string, description: string) => {
      e.preventDefault();
      e.stopPropagation();

      await createStoredPlaygroundSettings.trigger({
        json: {
          settings: getSettingsFromManifest(manifest),
          name,
          description,
          options: savedOptions,
        },
      });

      setIsNewConfigModalOpen(false);
    },
    [createStoredPlaygroundSettings, manifest, savedOptions]
  );

  const deleteStoredPlaygroundSettings = useDeletePlaygroundSettingsMutation(
    currentConfigId ?? '',
    {
      onSuccess: () => {
        createToast({
          title: 'Success',
          description: 'Deleted configuration',
          error: false,
        });
        setCurrentConfigId(undefined);
        resetToDefault();
      },
      onError: (e) => {
        createToast({
          title: 'Error',
          description: `Unable to delete configuration: ${e.message}`,
          error: true,
        });
      },
    }
  );

  const updateStoredPlaygroundSettings = useUpdatePlaygroundSettingsMutation(
    currentConfigId ?? '',
    {
      onSuccess: (data) => {
        setCurrentConfigId(data.id);
        createToast({
          title: 'Success',
          description: 'Updated configuration',
          error: false,
        });
      },
      onError: (e) => {
        createToast({
          title: 'Error',
          description: `Unable to update configuration: ${e.message}`,
          error: true,
        });
      },
    }
  );

  const hasSettingsChangedFromStoredVersion = useMemo(() => {
    const currentSettings = storedPlaygroundSettings?.find(
      (setting) => setting.id === currentConfigId
    );
    const manifestSettings = getSettingsFromModelManifest(
      manifest?.kwargs.last
    );

    return (
      !isEqual(currentSettings?.settings, manifestSettings) ||
      !isEqual(savedOptions, currentSettings?.options)
    );
  }, [
    storedPlaygroundSettings,
    manifest?.kwargs.last,
    savedOptions,
    currentConfigId,
  ]);

  const [defaultConfigId, setDefaultConfigId] = useLocalStorageState<
    string | undefined
  >(DEFAULT_CONFIG_ID_LOCAL_STORAGE_KEY, undefined);

  const isDefault = currentConfigId === defaultConfigId;
  const isSavedConfig = !!currentConfigId;

  const renderCrudButtons = () => {
    return (
      <div className="ml-auto flex flex-row items-center gap-2">
        {isSavedConfig && (
          <DeleteConfirmationButton
            onDelete={() => deleteStoredPlaygroundSettings.trigger({})}
            title={`Delete ${
              storedPlaygroundSettings?.find(
                (setting) => setting.id === currentConfigId
              )?.name
            }`}
            description="Are you sure you want to delete this configuration? This action cannot be undone."
            deleteButtonText="Delete"
            isDeleting={deleteStoredPlaygroundSettings.isMutating}
            trigger={
              <IconButton size="sm" color="danger" variant="outlined">
                <Trash04Icon className="my-auto h-4 w-4" />
              </IconButton>
            }
          />
        )}
        {isSavedConfig ? (
          <>
            <Button
              onClick={() => setIsNewConfigModalOpen(true)}
              variant="outlined"
              color="neutral"
              size="sm"
            >
              <span className="text-xs text-tertiary">Save as</span>
            </Button>
            <Tooltip
              title={
                !hasSettingsChangedFromStoredVersion
                  ? 'No changes to save'
                  : 'Update the currently selected configuration'
              }
            >
              <div>
                <Button
                  onClick={() =>
                    updateStoredPlaygroundSettings.trigger({
                      json: {
                        settings: getSettingsFromManifest(manifest),
                        options: savedOptions,
                      },
                    })
                  }
                  size="sm"
                  disabled={!hasSettingsChangedFromStoredVersion}
                >
                  <span className="text-xs">Save</span>
                </Button>
              </div>
            </Tooltip>
          </>
        ) : (
          <Button
            onClick={() => setIsNewConfigModalOpen(true)}
            variant="outlined"
            color="primary"
            size="sm"
          >
            <span className="text-xs">Save as</span>
          </Button>
        )}
      </div>
    );
  };

  const handleSelectConfig = (config: PlaygroundSettingsSchema) => {
    setCurrentConfigId(config.id);
    updateModelManifest(config.settings);
    setSavedOptions(config.options ?? {});
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row items-start justify-between pb-2">
        <div className="flex flex-row items-start gap-2">
          <ModelConfigurationDropdown
            showDropdown={showDropdown}
            currentConfigId={currentConfigId}
            defaultConfigId={defaultConfigId}
            storedPlaygroundSettings={storedPlaygroundSettings ?? []}
            onSelectConfig={handleSelectConfig}
          />
          <ModelConfigurationDefaultButton
            isDefault={isDefault}
            setDefaultConfigId={setDefaultConfigId}
            currentConfigId={currentConfigId}
            isSavedConfig={isSavedConfig}
          />
        </div>
        <div className="flex flex-row items-center gap-2">
          {renderCrudButtons()}
        </div>
      </div>

      <div className="flex flex-col overflow-y-auto py-2">{children}</div>

      {isNewConfigModalOpen && (
        <ConfigurationCrudModal
          currentConfigId={null}
          allSettings={storedPlaygroundSettings ?? []}
          isOpen={true}
          isLoading={createStoredPlaygroundSettings.isMutating}
          title="New configuration"
          setIsOpen={setIsNewConfigModalOpen}
          handleSubmit={handleNewConfigSubmit}
        />
      )}
    </div>
  );
}
